import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CircleDot, Loader2, UserRound, UsersRound } from 'lucide-react';
import { useAuth } from '../../../firebase/AuthProvider';
import { searchTasks } from '../../../services/taskServices/TaskSearchService';

interface TaskPreview {
    title: string;
    status?: string;
    participantName?: string;
    assigneeName?: string;
}

interface TaskProfileTooltipProps {
    taskReference: string;
    theme?: 'light' | 'dark';
}

const TaskProfileTooltip: React.FC<TaskProfileTooltipProps> = ({ taskReference, theme = 'light' }) => {
    const { currentUser, impersonatedUserId, currentOrganization, currentPolicies, currentUserInfo } = useAuth();
    const authScope = useMemo(() => ({ currentUser, impersonatedUserId, currentOrganization, currentPolicies, currentUserInfo }),
        [currentUser, impersonatedUserId, currentOrganization, currentPolicies, currentUserInfo]);
    const [resultState, setResultState] = useState<{ value: TaskPreview; scope: typeof authScope; reference: string } | null>(null);
    const task = resultState?.scope === authScope && resultState?.reference === taskReference ? resultState.value : null;
    const [isLoading, setIsLoading] = useState(!task);
    const [hasError, setHasError] = useState(false);
    const isDark = theme === 'dark';

    useEffect(() => {
        const controller = new AbortController();
        const friendlyId = taskReference.split('-').pop() ?? taskReference;
        setResultState(null);
        setIsLoading(true);
        setHasError(false);

        const loadTask = async () => {
            try {
                if (!currentUser) throw new Error('User is not authenticated');
                const authToken = await currentUser.getIdToken();
                const result = await searchTasks(authToken, { searchTerm: taskReference, page: 1, pageSize: 5 }, controller.signal);
                const match = result?.tasks.find(item => String(item.friendlyId) === friendlyId && Boolean(item.participant));
                if (!match) throw new Error('Participant task was not found');
                const preview: TaskPreview = {
                    title: match.title,
                    status: typeof match.status === 'string' ? match.status : match.status?.name,
                    participantName: match.participant ? `${match.participant.firstName} ${match.participant.lastName}`.trim() : undefined,
                    assigneeName: match.assignees?.[0]?.displayName,
                };
                if (!controller.signal.aborted) setResultState({ value: preview, scope: authScope, reference: taskReference });
            } catch (error) {
                if (!controller.signal.aborted) setHasError(true);
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };

        void loadTask();
        return () => controller.abort();
    }, [currentUser, authScope, taskReference]);

    const primary = isDark ? 'text-slate-100' : 'text-slate-800';
    const secondary = isDark ? 'text-slate-400' : 'text-slate-500';
    const divider = isDark ? 'border-slate-700/60' : 'border-slate-100';

    return (
        <div className="w-72 p-1 font-poppins">
            <div className={`border-b pb-3 ${divider}`}>
                <div className={`font-mono text-[11px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-[#6F49B5]'}`}>#{taskReference}</div>
                <div className={`mt-1 line-clamp-2 text-sm font-semibold leading-5 ${primary}`}>
                    {task?.title ?? (isLoading ? 'Loading task…' : 'Task unavailable')}
                </div>
            </div>
            <div className="relative flex min-h-[72px] flex-col justify-center gap-2 pt-3 text-xs">
                {isLoading ? (
                    <div className={`flex items-center justify-center gap-2 ${secondary}`} role="status"><Loader2 size={16} className="animate-spin" /> Loading details…</div>
                ) : hasError || !task ? (
                    <div className="flex items-center justify-center gap-2 text-amber-600 dark:text-amber-300"><AlertCircle size={16} /> Task details could not be loaded.</div>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-3"><span className={`flex items-center gap-1.5 ${secondary}`}><CircleDot size={14} /> Status</span><span className={`truncate font-semibold ${primary}`}>{task.status || 'Open'}</span></div>
                        <div className="flex items-center justify-between gap-3"><span className={`flex items-center gap-1.5 ${secondary}`}><UsersRound size={14} /> Participant</span><span className={`max-w-[150px] truncate ${primary}`}>{task.participantName || 'Unknown'}</span></div>
                        <div className="flex items-center justify-between gap-3"><span className={`flex items-center gap-1.5 ${secondary}`}><UserRound size={14} /> Assignee</span><span className={`max-w-[150px] truncate ${primary}`}>{task.assigneeName || 'Unassigned'}</span></div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TaskProfileTooltip;
